import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useAiStatus from '../hooks/useAiStatus';
import streamChat from '../api/aiStream';
import API from '../api/axios';

/*
 * Focusly AI chat.
 *
 * The server owns the transcript. A turn sends one message plus a conversation
 * id; what comes back is the full message array, which this component renders
 * but never edits. Rendering derives from that array -- tool_use, thinking and
 * tool_result blocks are internal machinery and are not shown as messages.
 */

const TOOL_LABELS = {
  get_workload_summary: 'Checking your workload',
  search_tasks: 'Searching tasks',
  search_notes: 'Searching notes',
  list_events: 'Checking your calendar',
  list_folders_and_tags: 'Looking up folders and tags',
  create_task: 'Preparing a new task',
  update_task: 'Preparing a task change',
};

// Which conversation the user was last in. Survives navigating away from the
// page and reloading the tab; the conversation itself lives in the database,
// so this only has to remember the id.
const LAST_CONVERSATION_KEY = 'focusly-ai-conversation';

function readLastConversation() {
  try { return localStorage.getItem(LAST_CONVERSATION_KEY); } catch { return null; }
}

function writeLastConversation(id) {
  try {
    if (id) localStorage.setItem(LAST_CONVERSATION_KEY, id);
    else localStorage.removeItem(LAST_CONVERSATION_KEY);
  } catch { /* private browsing; restoring is a convenience, not a requirement */ }
}

const SUGGESTIONS = [
  'What do I need to worry about the most?',
  'What is overdue?',
  'What does my week look like?',
];

// Assistant turns carry thinking and tool_use blocks alongside prose; only the
// text is renderable.
function assistantText(content) {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  return content.filter((b) => b.type === 'text').map((b) => b.text).join('');
}

function visibleMessages(transcript) {
  const out = [];
  for (const m of transcript) {
    if (m.role === 'user') {
      // Array content on a user turn is tool results being fed back, not
      // something the user typed.
      if (typeof m.content === 'string') out.push({ role: 'user', text: m.content });
      continue;
    }
    const text = assistantText(m.content);
    if (text.trim()) out.push({ role: 'assistant', text });
  }
  return out;
}

export default function FocuslyAIPage() {
  const { aiEnabled, loaded } = useAiStatus();

  const [transcript, setTranscript] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [activity, setActivity] = useState(null);
  const [pending, setPending] = useState(null);
  const [approvals, setApprovals] = useState({});
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const historyRef = useRef(null);

  const refreshConversations = useCallback(() => {
    API.get('/ai/conversations')
      .then(({ data }) => setConversations(data))
      .catch(() => { /* history is a convenience; a failure must not break chat */ });
  }, []);

  useEffect(() => { if (aiEnabled) refreshConversations(); }, [aiEnabled, refreshConversations]);

  // Reopen the last conversation when the page mounts, so navigating away and
  // back does not look like the chat was thrown away. A turn that was still
  // streaming when the user left is persisted server-side, so it reappears
  // finished rather than truncated.
  useEffect(() => {
    if (!loaded) return undefined;
    if (!aiEnabled) { setRestoring(false); return undefined; }
    const saved = readLastConversation();
    if (!saved) { setRestoring(false); return undefined; }

    let cancelled = false;
    API.get(`/ai/conversations/${saved}`)
      .then(({ data }) => {
        if (cancelled) return;
        setConversationId(data.id);
        setTranscript(data.messages);
      })
      // Deleted, or belonging to whoever used this browser last. Either way
      // the stale id is dropped and the user gets a fresh chat.
      .catch(() => writeLastConversation(null))
      .finally(() => { if (!cancelled) setRestoring(false); });
    return () => { cancelled = true; };
  }, [loaded, aiEnabled]);

  // Deliberately gated on `restoring`. useAiStatus reports loaded:false on the
  // first render, so the restore effect above no-ops on that pass -- and if
  // this ran then, it would persist the initial null and delete the very id
  // restore is about to read. Waiting until restore has settled means the
  // first value written is the restored one (or null when there was nothing
  // to restore).
  useEffect(() => {
    if (restoring) return;
    writeLastConversation(conversationId);
  }, [conversationId, restoring]);

  // Close the history popover on an outside click, matching how the folder
  // picker in TaskModal behaves.
  useEffect(() => {
    if (!historyOpen) return undefined;
    const onDown = (e) => {
      if (historyRef.current && !historyRef.current.contains(e.target)) setHistoryOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [historyOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [transcript, liveText, activity, pending]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const runTurn = useCallback(async (body) => {
    setStreaming(true);
    setError(null);
    setLiveText('');
    setPending(null);
    setActivity('Thinking');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChat(body, (type, data) => {
        if (type === 'status') { setActivity('Thinking'); if (data.conversation_id) setConversationId(data.conversation_id); }
        else if (type === 'tool') setActivity(TOOL_LABELS[data.name] || 'Working');
        else if (type === 'text') { setActivity(null); setLiveText((t) => t + data.delta); }
        else if (type === 'pending') {
          setTranscript(data.messages);
          setPending(data.proposals);
          setApprovals(Object.fromEntries(data.proposals.map((p) => [p.tool_use_id, true])));
          setActivity(null);
        } else if (type === 'done') { setTranscript(data.messages); setActivity(null); }
        else if (type === 'saved') refreshConversations();
        else if (type === 'error') { setError(data.message); setActivity(null); }
      }, controller.signal);
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message || 'Connection lost');
    } finally {
      // liveText is cleared here because the completed turn now lives in the
      // transcript; keeping both would render the answer twice.
      setLiveText('');
      setActivity(null);
      setStreaming(false);
      abortRef.current = null;
    }
  }, [refreshConversations]);

  const send = (text) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    // Shown immediately so the message does not appear to vanish while the
    // model thinks; the server's copy replaces it when the turn returns.
    setTranscript((t) => [...t, { role: 'user', content: trimmed }]);
    setInput('');
    runTurn({ conversation_id: conversationId, message: trimmed });
  };

  const resolveProposals = (accept) => {
    const decided = accept
      ? approvals
      : Object.fromEntries(Object.keys(approvals).map((k) => [k, false]));
    setPending(null);
    runTurn({ conversation_id: conversationId, approvals: decided });
  };

  const reset = () => {
    abortRef.current?.abort();
    setTranscript([]); setConversationId(null); setLiveText('');
    setPending(null); setError(null); setActivity(null); setHistoryOpen(false);
    inputRef.current?.focus();
  };

  const openConversation = async (id) => {
    abortRef.current?.abort();
    setHistoryOpen(false);
    setError(null); setPending(null); setLiveText(''); setActivity(null);
    try {
      const { data } = await API.get(`/ai/conversations/${id}`);
      setConversationId(data.id);
      setTranscript(data.messages);
    } catch {
      setError('Could not open that conversation.');
    }
  };

  const removeConversation = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await API.delete(`/ai/conversations/${id}`);
      if (id === conversationId) reset();
      refreshConversations();
    } catch {
      setError('Could not delete that conversation.');
    }
  };

  if (!loaded) {
    return <div className="container py-4"><div className="text-muted">Loading…</div></div>;
  }

  if (!aiEnabled) {
    return (
      <div className="container py-4" style={{ maxWidth: 720 }}>
        <h4 className="mb-3">Focusly AI</h4>
        <div className="alert alert-info mb-0">
          Focusly AI is not configured on this deployment. It needs an Anthropic
          API key set on the server before it can be used.
        </div>
      </div>
    );
  }

  const messages = visibleMessages(transcript);
  const empty = messages.length === 0 && !liveText && !activity && !restoring;

  return (
    <div className="d-flex flex-column h-100">
      <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
        <strong>Focusly AI</strong>
        <div className="d-flex align-items-center gap-2">
          {/* Hand-rolled rather than a Bootstrap dropdown: this needs per-row
              delete buttons, and it avoids depending on the Bootstrap JS
              bundle for a menu inside a scrolling column. */}
          <div className="position-relative" ref={historyRef}>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setHistoryOpen((o) => !o)}
              aria-expanded={historyOpen}
            >
              History{conversations.length ? ` (${conversations.length})` : ''}
            </button>
            {historyOpen && (
              <div
                className="card shadow position-absolute end-0 mt-1"
                style={{ width: 300, maxHeight: 360, overflowY: 'auto', zIndex: 1000 }}
              >
                {conversations.length === 0 ? (
                  <div className="p-3 text-muted small">No past conversations yet.</div>
                ) : (
                  <div className="list-group list-group-flush">
                    {conversations.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${
                          c.id === conversationId ? 'active' : ''
                        }`}
                        onClick={() => openConversation(c.id)}
                      >
                        <span className="flex-grow-1 text-truncate text-start">{c.title}</span>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label="Delete conversation"
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={(e) => removeConversation(c.id, e)}
                          onKeyDown={(e) => { if (e.key === 'Enter') removeConversation(c.id, e); }}
                        >
                          ×
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {messages.length > 0 && (
            <button className="btn btn-sm btn-outline-secondary" onClick={reset} disabled={streaming}>
              New chat
            </button>
          )}
        </div>
      </div>

      <div className="flex-grow-1 overflow-auto px-3 py-3" style={{ minHeight: 0 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {empty && (
            <div className="text-center py-5">
              <div className="fs-4 mb-2">✨</div>
              <p className="text-muted mb-3">
                Ask about your tasks, notes and calendar.
              </p>
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="btn btn-sm btn-outline-secondary" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {restoring && (
            <div className="text-center text-muted py-5">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              <small>Restoring your conversation…</small>
            </div>
          )}

          {messages.map((m, i) =>
            m.role === 'user' ? (
              <div key={i} className="d-flex justify-content-end mb-3">
                <div
                  className="px-3 py-2 rounded"
                  style={{ maxWidth: '85%', backgroundColor: 'var(--fx-primary)', color: '#fff' }}
                >
                  {m.text}
                </div>
              </div>
            ) : (
              <div key={i} className="mb-3 fx-ai-prose">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
              </div>
            )
          )}

          {liveText && (
            <div className="mb-3 fx-ai-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{liveText}</ReactMarkdown>
            </div>
          )}

          {activity && (
            <div className="d-flex align-items-center gap-2 text-muted mb-3">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              <small>{activity}…</small>
            </div>
          )}

          {pending && (
            <div className="card mb-3">
              <div className="card-body">
                <div className="fw-medium mb-1">Approve this change?</div>
                <p className="text-muted small">
                  Focusly AI will not modify anything until you approve it.
                </p>
                {pending.map((p) => (
                  <div className="form-check" key={p.tool_use_id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={p.tool_use_id}
                      checked={approvals[p.tool_use_id] ?? false}
                      onChange={(e) =>
                        setApprovals((a) => ({ ...a, [p.tool_use_id]: e.target.checked }))
                      }
                    />
                    <label className="form-check-label" htmlFor={p.tool_use_id}>
                      {p.summary}
                    </label>
                  </div>
                ))}
                <div className="d-flex gap-2 mt-3">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => resolveProposals(true)}
                    disabled={!Object.values(approvals).some(Boolean)}
                  >
                    Apply selected
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => resolveProposals(false)}>
                    Decline
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && <div className="alert alert-danger">{error}</div>}
          <div ref={bottomRef} />
        </div>
      </div>

      <div className="border-top px-3 py-3">
        <form
          style={{ maxWidth: 760, margin: '0 auto' }}
          onSubmit={(e) => { e.preventDefault(); send(input); }}
        >
          <div className="input-group">
            <input
              ref={inputRef}
              className="form-control"
              placeholder={pending ? 'Respond to the pending change above…' : 'Ask about your tasks, notes or calendar…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={streaming || Boolean(pending)}
              autoFocus
            />
            <button
              className="btn btn-primary"
              type="submit"
              disabled={streaming || Boolean(pending) || !input.trim()}
            >
              Send
            </button>
          </div>
          <small className="text-muted d-block mt-2">
            Focusly AI can read your tasks, notes and events. Changes always need your approval.
          </small>
        </form>
      </div>
    </div>
  );
}
